/**
 * Main Application Entry Point
 *
 * This file initializes the Vue.js application with Inertia.js for SPA functionality.
 */

// Import bootstrap (axios, CSRF token setup, etc.)
import './bootstrap';

// Import global styles
import '../css/app.css';

// Vue imports
import { createApp, h } from 'vue';

// Inertia imports
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';

// Import Ziggy for Laravel route helpers in Vue (if available)
// Uncomment when Ziggy is installed
// import { ZiggyVue } from '../../vendor/tightenco/ziggy/dist/vue.m';

// Import custom directives
import clickAway from '@/Directives/clickAway';
import canDirective from '@/Directives/can';

// Import route helper
import route from '@/Utils/route';

// Get application name from page title or use default
const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'IT Helpdesk SaaS';

// Create Inertia app
createInertiaApp({
    // Page title configuration
    title: (title) => title ? `${title} - ${appName}` : appName,

    // Page component resolution with dynamic imports
    resolve: async (name) => {
        // Use Vite's glob import for dynamic page loading
        const pages = import.meta.glob('./Pages/**/*.vue');

        // Resolve the page component
        const page = await resolvePageComponent(
            `./Pages/${name}.vue`,
            pages
        );

        // Automatically assign default layout if page doesn't specify one
        // Check if layout property exists (not just if it's falsy)
        // This allows pages to explicitly set layout: null to disable layout
        if (page.default.layout === undefined) {
            // Determine layout based on page path
            if (name.startsWith('SiteOwner/Auth/')) {
                // Site owner authentication pages use no layout (full-page design)
                page.default.layout = null;
            } else if (name.startsWith('SiteOwner/')) {
                // Site owner dashboard pages use SiteOwnerLayout
                const SiteOwnerLayout = (await import('@layouts/SiteOwnerLayout.vue')).default;
                page.default.layout = SiteOwnerLayout;
            } else if (name.startsWith('Auth/')) {
                // Regular authentication pages use AuthLayout
                const AuthLayout = (await import('@layouts/AuthLayout.vue')).default;
                page.default.layout = AuthLayout;
            } else {
                // All other pages use DashboardLayout
                const DashboardLayout = (await import('@layouts/DashboardLayout.vue')).default;
                page.default.layout = DashboardLayout;
            }
        }

        return page;
    },

    // Set up Vue app
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        // Use Inertia plugin
        app.use(plugin);

        // Use Ziggy for Laravel routes (uncomment when installed)
        // app.use(ZiggyVue, Ziggy);

        // Register global directives
        app.directive('click-away', clickAway);
        app.directive('can', canDirective);
        app.directive('cannot', canDirective);

        // Register global error handler
        app.config.errorHandler = async (err, instance, info) => {
            // Log error to console in development
            if (import.meta.env.DEV) {
                console.error('Global error handler:', err);
                console.error('Component:', instance);
                console.error('Error info:', info);
            }

            // Show user-friendly error notification
            try {
                const { useErrorHandler } = await import('@/Composables/useErrorHandler.js');
                const { handleError } = useErrorHandler();
                handleError(err, {
                    title: 'Application Error',
                    showToast: true,
                });
            } catch (handlerError) {
                console.error('Failed to load error handler:', handlerError);
            }

            // TODO: Send errors to logging service in production
            if (import.meta.env.PROD) {
                // Example: Send to Sentry, Bugsnag, etc.
                // Sentry.captureException(err);
            }
        };

        // Register global warning handler (development only)
        if (import.meta.env.DEV) {
            app.config.warnHandler = (msg, instance, trace) => {
                console.warn('Vue warning:', msg);
                console.warn('Trace:', trace);
            };
        }

        // Global properties - make useful utilities available in all components
        app.config.globalProperties.$filters = {
            // Currency formatter
            currency(value, currency = 'USD') {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                }).format(value);
            },

            // Date formatter
            date(value, options = {}) {
                const defaultOptions = {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                };
                return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(new Date(value));
            },

            // Date time formatter
            datetime(value, options = {}) {
                const defaultOptions = {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                };
                return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(new Date(value));
            },

            // Truncate text
            truncate(value, length = 100) {
                if (!value) return '';
                if (value.length <= length) return value;
                return value.substring(0, length) + '...';
            },

            // Capitalize first letter
            capitalize(value) {
                if (!value) return '';
                return value.charAt(0).toUpperCase() + value.slice(1);
            },

            // Uppercase
            uppercase(value) {
                if (!value) return '';
                return value.toUpperCase();
            },

            // Lowercase
            lowercase(value) {
                if (!value) return '';
                return value.toLowerCase();
            },
        };

        // Make shared data easily accessible
        // Access via: this.$page.props.auth, this.$page.props.flash, etc.
        app.config.globalProperties.$user = () => {
            return app.config.globalProperties.$page.props.auth?.user || null;
        };

        app.config.globalProperties.$can = (permission) => {
            const permissions = app.config.globalProperties.$page.props.auth?.permissions || [];
            return permissions.includes(permission);
        };

        app.config.globalProperties.$hasRole = (role) => {
            const roles = app.config.globalProperties.$page.props.auth?.roles || [];
            return roles.includes(role);
        };

        // Register route helper globally
        app.config.globalProperties.route = route;

        // Register global components here if needed
        // Example:
        // import Button from '@components/Common/Button.vue';
        // app.component('Button', Button);

        // Mount the app
        return app.mount(el);
    },

    // Inertia progress bar configuration
    progress: {
        // Progress bar color (primary color from Tailwind config)
        color: '#2563eb',

        // Show progress bar after delay (milliseconds)
        delay: 250,

        // Include default CSS
        includeCSS: true,

        // Show spinner
        showSpinner: false,
    },

    // Global Inertia error handler
    // Handles errors from failed Inertia requests before they bubble to global handlers
    onError: (errors) => {
        // Inertia automatically handles validation errors and redirects
        // We only need to prevent errors from bubbling to global handlers
        // This prevents duplicate error messages

        // Return false to prevent default error handling
        // (Inertia will still show validation errors via form.errors)
        return false;
    },
}).then(async () => {
    // Setup global error handling after app initialization
    const { setupGlobalErrorHandling } = await import('@/Composables/useErrorHandler.js');
    setupGlobalErrorHandling();

    // Log app initialization in development
    if (import.meta.env.DEV) {
        console.log('✨ Vue + Inertia application initialized');
        console.log('Environment:', import.meta.env.MODE);
        console.log('🛡️ Global error handling enabled');
    }
});
